/** Procedural canvas textures (no external assets needed). */
import * as THREE from 'three';

function canvasTexture(size: number, draw: (ctx: CanvasRenderingContext2D, size: number) => void): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  draw(ctx, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Soft radial glow, white (tinted by the material colour). */
export function glowTexture(): THREE.Texture {
  return canvasTexture(128, (ctx, s) => {
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    // roughly exponential falloff, so no ring shows at the sprite's edge (visible once the scene is tone-mapped, S2-08)
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.1, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.2)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.06)');
    g.addColorStop(0.7, 'rgba(255,255,255,0.015)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
  });
}

/** Filled disc with an antialiased edge: body markers at minimum size. */
export function discTexture(): THREE.Texture {
  return canvasTexture(64, (ctx, s) => {
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(s / 2, s / 2, s / 2 - 3, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** Spacecraft type groups with their own icon (icon = spacecraft type, Concept §1; legend S3-20). */
export type IconType = 'flyby' | 'orbiter' | 'probe' | 'crewed';

/** The icon group of a spacecraft type from the data. */
export function iconTypeOf(type: string): IconType {
  if (type === 'orbiter') return 'orbiter';
  if (type === 'crewed') return 'crewed';
  if (type === 'probe' || type === 'lander' || type === 'impactor' || type === 'rover') return 'probe';
  return 'flyby';
}

/**
 * Spacecraft icons by type: flyby = ring with a centre dot, orbiter = dot inside a tilted orbit ellipse, probe or
 * lander = a downward triangle, crewed = a capsule (a cone with a rounded heat shield, Sprint 4 D12). `silent`
 * (contact lost, D11) draws the outline only, without the solid centre.
 */
export function spacecraftIconTexture(type: IconType, silent = false): THREE.Texture {
  return canvasTexture(64, (ctx, s) => {
    const c = s / 2;
    if (!silent) {
      const glow = ctx.createRadialGradient(c, c, s * 0.2, c, c, c);
      glow.addColorStop(0, 'rgba(255,255,255,0.35)');
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, s, s);
    }
    ctx.strokeStyle = '#fff';
    ctx.fillStyle = '#fff';
    ctx.lineWidth = s * 0.09;
    ctx.beginPath();
    if (type === 'orbiter') {
      ctx.ellipse(c, c, s * 0.38, s * 0.2, -0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(c, c, s * 0.13, 0, Math.PI * 2);
      if (silent) ctx.stroke();
      else ctx.fill();
      return;
    }
    if (type === 'crewed') {
      ctx.moveTo(c - s * 0.11, c - s * 0.3);
      ctx.lineTo(c + s * 0.11, c - s * 0.3);
      ctx.lineTo(c + s * 0.33, c + s * 0.14);
      ctx.quadraticCurveTo(c, c + s * 0.36, c - s * 0.33, c + s * 0.14);
      ctx.closePath();
      if (silent) ctx.stroke();
      else ctx.fill();
      return;
    }
    if (type === 'probe') {
      ctx.moveTo(c - s * 0.3, c - s * 0.22);
      ctx.lineTo(c + s * 0.3, c - s * 0.22);
      ctx.lineTo(c, c + s * 0.3);
      ctx.closePath();
      if (silent) ctx.stroke();
      else ctx.fill();
      return;
    }
    ctx.arc(c, c, s * 0.3, 0, Math.PI * 2);
    ctx.stroke();
    if (silent) return;
    ctx.beginPath();
    ctx.arc(c, c, s * 0.11, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** End marker (D11): where a trajectory ended (Cassini's plunge, Huygens' landing) — a small cross. */
export function endMarkerTexture(): THREE.Texture {
  return canvasTexture(64, (ctx, s) => {
    const c = s / 2;
    const r = s * 0.26;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = s * 0.1;
    ctx.lineCap = 'square';
    ctx.beginPath();
    ctx.moveTo(c - r, c - r);
    ctx.lineTo(c + r, c + r);
    ctx.moveTo(c + r, c - r);
    ctx.lineTo(c - r, c + r);
    ctx.stroke();
  });
}

/** Event marker: a diamond outline (flyby / closest approach). */
export function diamondTexture(): THREE.Texture {
  return canvasTexture(64, (ctx, s) => {
    const c = s / 2;
    const r = s * 0.36;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = s * 0.08;
    ctx.beginPath();
    ctx.moveTo(c, c - r);
    ctx.lineTo(c + r, c);
    ctx.lineTo(c, c + r);
    ctx.lineTo(c - r, c);
    ctx.closePath();
    ctx.stroke();
  });
}

let placeholder: THREE.Texture | null = null;

/** A shared 1×1 white texture: lets a material compile its "with map" shader before the real image arrives. */
export function placeholderTexture(): THREE.Texture {
  if (!placeholder) {
    placeholder = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    placeholder.colorSpace = THREE.SRGBColorSpace;
    placeholder.needsUpdate = true;
  }
  return placeholder;
}
